import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Turtle, Target, CheckCircle, Clock, MessageSquare,
  TrendingUp, BookOpen, Gamepad2, History, LogIn
} from 'lucide-react';
import api from '../services/api';
import { authAPI } from '../services/api';

interface DashboardData {
  totalGames: number;
  correctGames: number;
  uniqueStoriesPlayed: number;
  totalStoriesAvailable: number;
  totalQuestions: number;
  totalDuration: number;
  winRate: number;
  byMode: Record<string, { played: number; won: number }>;
  recentGames?: Array<{
    _id: string;
    storyTitle: string;
    mode: string;
    correct: boolean;
    createdAt: string;
  }>;
}

const modeLabels: Record<string, { label: string; icon: React.ReactNode }> = {
  single: { label: '海龟汤推理', icon: <Turtle className="w-4 h-4" /> },
  dual: { label: '双线叙事', icon: <BookOpen className="w-4 h-4" /> },
  mentor: { label: '导师故事', icon: <Target className="w-4 h-4" /> },
  scenario: { label: '情景模拟', icon: <Gamepad2 className="w-4 h-4" /> },
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
      return;
    }
    setIsLoggedIn(true);
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      // 先获取 dashboard
      const dashRes = await api.get('/user/dashboard');
      // 同时获取历史记录的前几条作为最近游戏
      const histRes = await api.get('/user/history', { params: { limit: 5 } });
      setData({
        ...dashRes.data.data,
        recentGames: histRes.data.data || []
      });
    } catch (err) {
      console.error('获取仪表盘数据失败:', err);
      if ((err as any)?.response?.status === 401) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white">
        <nav className="glass sticky top-0 z-50 border-b border-gray-200">
          <div className="container mx-auto px-4 py-4 flex items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <Turtle className="w-6 h-6 text-ocean-600" />
              <span className="text-lg font-bold text-ocean-600">法底捞针</span>
            </button>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <LogIn className="w-16 h-16 text-ocean-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">请先登录</h2>
            <p className="text-gray-500 mb-6">登录后查看你的反诈学习进度</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-ocean-600 text-white rounded-xl hover:bg-ocean-700 transition-colors"
            >
              返回首页
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white flex items-center justify-center">
        <p className="text-gray-500">加载失败，请刷新重试</p>
      </div>
    );
  }

  const progressPercent = data.totalStoriesAvailable > 0
    ? Math.round((data.uniqueStoriesPlayed / data.totalStoriesAvailable) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-turtle-50 to-white">
      {/* 导航栏 */}
      <nav className="glass sticky top-0 z-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <Turtle className="w-6 h-6 text-ocean-600" />
              <span className="text-lg font-bold text-ocean-600">法底捞针</span>
            </button>
            <button
              onClick={() => navigate('/history')}
              className="flex items-center space-x-1 text-sm text-ocean-600 hover:text-ocean-800 transition-colors"
            >
              <History className="w-4 h-4" />
              <span>历史记录</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">我的反诈学习</h1>
          <p className="text-gray-500">追踪你的海龟汤推理进度</p>
        </motion.div>

        {/* 核心数据卡片 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <StatCard
            icon={<Gamepad2 className="w-5 h-5" />}
            label="总游戏数"
            value={data.totalGames}
            color="bg-ocean-50 text-ocean-700"
          />
          <StatCard
            icon={<CheckCircle className="w-5 h-5" />}
            label="正确率"
            value={`${data.winRate}%`}
            color="bg-green-50 text-green-700"
          />
          <StatCard
            icon={<MessageSquare className="w-5 h-5" />}
            label="总提问数"
            value={data.totalQuestions}
            color="bg-purple-50 text-purple-700"
          />
          <StatCard
            icon={<Clock className="w-5 h-5" />}
            label="耗时(分钟)"
            value={data.totalDuration}
            color="bg-warm-50 text-warm-700"
          />
        </motion.div>

        {/* 进度条 + 统计 */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* 探索进度 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-ocean-600" />
                探索进度
              </h3>
              <span className="text-sm text-gray-400">
                {data.uniqueStoriesPlayed}/{data.totalStoriesAvailable} 题
              </span>
            </div>
            <div className="relative">
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-ocean-400 to-ocean-600 rounded-full"
                />
              </div>
              <span className="absolute right-0 -top-6 text-sm font-bold text-ocean-600">
                {progressPercent}%
              </span>
            </div>
            <div className="mt-4 flex justify-between text-sm text-gray-500">
              <span>已玩过 {data.uniqueStoriesPlayed} 个题目</span>
              <span>共 {data.totalStoriesAvailable} 个题目</span>
            </div>
          </motion.div>

          {/* 胜负统计 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-green-600" />
              成绩概览
            </h3>
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-28 h-28">
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <motion.circle
                    cx="50" cy="50" r="42" fill="none" stroke="url(#winGrad)"
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 42}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - data.winRate / 100) }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </svg>
                <svg width="0" height="0">
                  <defs>
                    <linearGradient id="winGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#22c55e" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center flex-col">
                  <span className="text-2xl font-bold text-gray-800">{data.winRate}%</span>
                  <span className="text-xs text-gray-400">正确率</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">{data.correctGames}</div>
                <div className="text-xs text-gray-400">答对</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{data.totalGames - data.correctGames}</div>
                <div className="text-xs text-gray-400">未通过</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* 各模式统计 */}
        {Object.keys(data.byMode).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-purple-600" />
              各模式统计
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.byMode).map(([mode, stats]) => (
                <div key={mode} className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="flex justify-center mb-2">
                    {modeLabels[mode]?.icon || <Gamepad2 className="w-4 h-4" />}
                  </div>
                  <div className="text-sm text-gray-500 mb-1">{modeLabels[mode]?.label || mode}</div>
                  <div className="text-xl font-bold text-gray-800">{stats.won}/{stats.played}</div>
                  <div className="text-xs text-gray-400">
                    正确率 {stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* 最近游戏 */}
        {data.recentGames && data.recentGames.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-ocean-600" />
              最近游戏
            </h3>
            <div className="space-y-3">
              {data.recentGames.map((game) => (
                <div
                  key={game._id}
                  className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${game.correct ? 'bg-green-500' : 'bg-red-400'}`} />
                    <div>
                      <div className="text-sm font-medium text-gray-800">{game.storyTitle}</div>
                      <div className="text-xs text-gray-400">
                        {modeLabels[game.mode]?.label || game.mode} · {new Date(game.createdAt).toLocaleDateString('zh-CN')}
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${game.correct ? 'text-green-600' : 'text-red-400'}`}>
                    {game.correct ? '✓ 破解' : '✗ 未解'}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// 统计卡片子组件
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}> = ({ icon, label, value, color }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-center">
    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3 ${color}`}>
      {icon}
    </div>
    <div className="text-2xl font-bold text-gray-800 mb-1">{value}</div>
    <div className="text-xs text-gray-400">{label}</div>
  </div>
);

export default DashboardPage;
